import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import FullScreenLoader from "Components/Common/FullScreenLoader";
import { emailUpdateStatusApi, viewEmailInvoiceApi } from "helpers/ApiManager";
import { ViewInvoicePayload } from "types/email.api";
import { ErrorHandler, SuccessToast } from "utils/toast";
import DocumentViewLayoutCopy from "Components/DocumentViewLayoutCopy";
import { createPayment, regeneratePaymentLink } from "slices/thunks";
import { useAppDispatch } from "Components/Hooks/hooks";

const ViewInvoice = () => {
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get("invoice_id");
  const token = searchParams.get("token");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPayInvoiceLoading, setIsPayInvoiceLoading] = useState(false);
  const dispatch = useAppDispatch()

  const navigate = useNavigate();

  const [invoiceDetails, setInvoiceDetails] =
    useState<ViewInvoicePayload | null>(null);

  const [isFetching, setIsFetching] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);

  useEffect(() => {
    if (!invoiceId || !token) {
      setIsFetching(false);
      return;
    }

    const fetchInvoice = async () => {
      try {
        const res = await viewEmailInvoiceApi({
          invoiceId: parseInt(invoiceId),
          hash: token,
        });

        if (res) {
          setInvoiceDetails(res.payload);
        }
      } catch (error) {
        ErrorHandler(error);
        navigate("/dashboard");
      } finally {
        setIsFetching(false);
      }
    };

    fetchInvoice();
  }, [invoiceId, token, navigate]);

  const columns = useMemo(
    () => [
      {
        header: "Description",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "Qty",
        accessorKey: "quantity",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: any) => {
          const { quantity, unit } = row.original;
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {quantity} {unit}
            </span>
          );
        },
      },
      {
        header: "Unit Price",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{cell.getValue().toFixed(2)}
          </span>
        ),
      },
      {
        header: "Total",
        accessorKey: "total_price",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            £{cell.getValue().toFixed(2)}
          </span>
        ),
      },
    ],
    [],
  );

  const handleInvoiceAction = async (status: "approved" | "rejected") => {
    if (!invoiceId || !token) return;

    try {
      setIsActionLoading(true);

      await emailUpdateStatusApi({
        invoiceId: invoiceId,
        hash: token,
        status,
      });

      SuccessToast(`Invoice marked as ${status}`);
      navigate("/status-quote-invoice");
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!invoiceDetails) return;
    try {
      setIsDownloading(true);
      const downloadUrl = `${invoiceDetails.invoice.url}?download=1`;
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${invoiceDetails.invoice.invoice_number}.pdf`;
      link.click();
      URL.revokeObjectURL(downloadUrl);
      link.remove();
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePayDeposit = async () => {
    if (!invoiceDetails) return;

    try {
      setIsPayInvoiceLoading(true);

      if (invoiceDetails.deposit_payment?.id && invoiceDetails.deposit_payment.status !== "received") {
        const response = await dispatch(
          regeneratePaymentLink({ id: invoiceDetails.deposit_payment.id }),
        )?.unwrap();
        const url = response?.payload?.stripe_payment_link_url;
        if (url) {
          window.location.href = url;
        }
      } else {
        const payload = {
          client_id: invoiceDetails.invoice.customer_id,
          amount: invoiceDetails.invoice.financial_summary?.grand_total ?? 0,
          amount_type: "invoice",
          payment_method: "stripe",
          currency: "GBP",
          payment_date: new Date().toISOString().split("T")[0],
          invoice_id: invoiceDetails.invoice.id,
          order_id: "",
        };
        const response = await dispatch(createPayment(payload))?.unwrap();
        const url = response?.payload?.payment?.stripe_payment_link_url;
        if (url) {
          window.location.href = url;
        }
      }
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsPayInvoiceLoading(false);
    }
  };

  if (isFetching) {
    return <FullScreenLoader isLoading message="Fetching Invoice Details..." />;
  }

  if (!invoiceId || !token || !invoiceDetails) {
    return <Navigate to="/dashboard" replace />;
  }


  return (
    <>
      <FullScreenLoader
        isLoading={isActionLoading}
        message="Processing your action..."
      />

      <DocumentViewLayoutCopy
        invoiceDetails={invoiceDetails}
        title="Invoice Details"
        originator={{
          name: invoiceDetails.originator.name,
        }}
        company={invoiceDetails.company}
        documentNumberLabel="Invoice Number"
        documentNumber={invoiceDetails.invoice.invoice_number}
        documentDateLabel="Invoice Date"
        documentDate={invoiceDetails.invoice.invoice_date}
        dueDate={invoiceDetails.invoice.due_date}
        items={invoiceDetails.invoice.items}
        columns={columns}
        financialSummary={invoiceDetails.invoice.financial_summary}
        quoteInvoiceSettings={invoiceDetails.quote_invoice_settings}
        handleAction={handleInvoiceAction}
        btnName="Invoice"
        showButtons={false}
        onDownload={handleDownload}
        isDownloading={isDownloading}
        paymentStatus={invoiceDetails?.deposit_payment?.status ?? "pending"}
        showPayDepositButton={
          invoiceDetails?.invoice.payment_method !== "cash" &&
          (invoiceDetails.invoice.status === "overdue" ||
            invoiceDetails.invoice.status === "due")

        }
        onPayDeposit={handlePayDeposit}
        payAmount={
          (invoiceDetails.invoice.financial_summary?.grand_total ?? 0) - (invoiceDetails?.deposit_payment?.amount ?? 0)
        }
        isPayLoading={isPayInvoiceLoading}
      />
    </>
  );
};

export default ViewInvoice;
